import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useCurrentUser } from '../../../hooks/useCurrentUser/useCurrentUser';
import {
  CSGetEntity,
  CSGetRecentClients,
  Entity,
  recentClientsInVar,
} from '@admiin-com/ds-graphql';
import React from 'react';

export const useRecentClients = () => {
  const user = useCurrentUser();
  const hasClients = !!user.firmId;

  const { data: recentClientIdsData } = useQuery(gql(CSGetRecentClients));
  const recentClientIds: string[] = React.useMemo(
    () => recentClientIdsData?.recentClients || [],
    [recentClientIdsData]
  );

  const [getEntityQuery, { loading }] = useLazyQuery(gql(CSGetEntity));

  const [displayedClientEntities, setDisplayedClientEntities] = React.useState<
    Entity[]
  >([]);

  React.useEffect(() => {
    const fetchEntities = async () => {
      const failedClientIds: string[] = [];
      const successfulEntities: Entity[] = [];

      // Process each client ID sequentially
      for (const clientId of recentClientIds) {
        try {
          const result = await getEntityQuery({
            variables: { id: clientId },
          });

          const entity = result.data?.getEntity;
          if (entity) {
            successfulEntities.push({
              ...entity,
              id: entity.id,
            });
          } else {
            failedClientIds.push(clientId);
          }
        } catch (error) {
          console.log(`Failed to fetch client ${clientId}:`, error);
          failedClientIds.push(clientId);
        }
      }

      // Remove failed client IDs from recent clients
      if (failedClientIds.length > 0) {
        console.log('Failed client IDs:', failedClientIds);

        const newRecentClientIds = recentClientIds.filter(
          (clientId) => !failedClientIds.includes(clientId)
        );
        recentClientsInVar(newRecentClientIds);
        localStorage.setItem(
          'recentClients',
          JSON.stringify(newRecentClientIds)
        );
      }

      setDisplayedClientEntities(successfulEntities);
    };

    fetchEntities();
  }, [recentClientIds, getEntityQuery]);

  return { loading, recentClients: hasClients ? displayedClientEntities : [] };
};
